// @flow
import React, { Component } from 'react';
import { FormValidator } from '../../../../utils/form';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Element } from 'react-scroll';
import classNames from 'classnames/bind';
import Dropzone from 'react-dropzone';
import RadioButton from '../../../../elements/RadioButton/RadioButton';
import Modal from '../../../../elements/Modal/Modal';

import style from './WishListModal.scss';
import icons from '../../../../assets/styles/icons.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import inputStyle from '../../../../assets/styles/input.scss';

type Props = {
  +mode: 'edit' | 'create';
  +handleCloseModal: () => void;
  +openModal?: boolean;
  +createWishList: () => void;
  +editWishList: () => void;
  +handleDeleteImage: () => void;
  +handleChangeImage: (acceptedFiles: File[]) => void;
  +handleChangeInputText: (e: Event) => void;
  +handleFocusInputText: (e: Event) => void;
  +handleChangeTypeOption: (index: number) => void;
  +validateForm: () => boolean;
  +name: {
    value: string;
    errors?: string[];
  };
  +image: {
    value: string;
    valueURI?: string;
    errors?: string[];
  };
  +wishListTypeOption: {
    value: number;
    errors?: string[];
  };
};

type State = {};

const wishListTypeOptions = [
  {
    value: 'PRIVATE',
    intl: 'myWishLists.createModal.privacy.private',
  },
  {
    value: 'UNLISTED',
    intl: 'myWishLists.createModal.privacy.shared',
  },
  {
    value: 'PUBLIC',
    intl: 'myWishLists.createModal.privacy.public',
  },
];

export default class WishListModal extends Component<Props, State> {

  handleClickDeleteImage = (e: Event) => {
    const { handleDeleteImage } = this.props;
    e.preventDefault();
    e.stopPropagation();
    handleDeleteImage();
  };

  handleClickMainButton = () => {
    const { createWishList, editWishList, handleCloseModal, mode, validateForm } = this.props;
    const actionFn = mode === 'create' ? createWishList : editWishList;
    const isValid = validateForm();
    if (!isValid) {
      console.log('handleClickPostWishList error!!');
    } else {
      actionFn();
      handleCloseModal();
    }
  };
  
  handleChangeName = (e: Event) => {
    const { handleChangeInputText } = this.props;
    if (e.target instanceof HTMLInputElement) {
      if (e.target.value.length <= 30) {
        handleChangeInputText(e);
      }
    }
  };
  
  render() {
    const {
      name, image, wishListTypeOption, handleChangeImage, mode, handleChangeInputText, handleFocusInputText,
      handleChangeTypeOption, openModal, handleCloseModal
    } = this.props;
    const nameError = !!name.errors && FormValidator.buildErrorLabel(name.errors, {
      required: 'myWishLists.createModal.name.error',
    });
    const imageError = !!image.errors && FormValidator.buildErrorLabel(image.errors, {
      required: 'myWishLists.createModal.upload.error',
    });
    const wishListTypeOptionError = !!wishListTypeOption.errors && FormValidator.buildErrorLabel(wishListTypeOption.errors, {
      required: 'myWishLists.createModal.privacy.error',
    });

    const dropZone = (
      <article className={style.dropZoneWrap}>
        {(image.value || image.valueURI) &&
        <button key="newOrderImageDelete"
                type="button"
                className={style.dropZoneDeleteImage}
                onClick={this.handleClickDeleteImage}>
          <i className={icons.iconCloseLight}/>
        </button>}
        <Element name="image">
          <Dropzone key="dropzone"
                    accept="image/jpeg,image/jpg,image/png"
                    multiple={false}
                    className={classNames(style.dropZone, {
                      [style.error]: image && image.errors,
                    })}
                    activeClassName={style.active}
                    disabledClassName={style.disabled}
                    maxSize={2097152}
                    onDrop={handleChangeImage}>
            {(image && (image.value || image.valueURI)) &&
            <img src={image.value || image.valueURI}
                 alt={name.value || ''}/>
            }
            {(!image || (!image.value && !image.valueURI)) &&
            <section className={style.dropZoneContent}>
              <i className={icons.symbolPlus}/>
              <FormattedMessage id="myWishLists.createModal.upload">{
                txt => <p className={style.dropZoneTitle}>{txt}</p>
              }</FormattedMessage>
            </section>}
          </Dropzone>
          {imageError}
        </Element>
      </article>
    );

    return (
      <Modal open={openModal}
             handleClose={handleCloseModal}
             width={544}
             height={712}>
        <section className={style.wishListModal}>
          <FormattedMessage id={mode === 'create' ? 'myWishLists.createModal.title' : 'myWishLists.editModal.title'}>{
            txt => (<h2>{txt}</h2>)
          }</FormattedMessage>
          {dropZone}
          <Element name="name">
            <article className={style.name}>
              <FormattedMessage id="myWishLists.createModal.name.label">{
                txt => (<label>{txt}:</label>)
              }</FormattedMessage>
              <FormattedMessage id="myWishLists.createModal.name.placeholder">{
                txt => <input type="text"
                              name="name"
                              id="name"
                              autoComplete="disabled"
                              value={name.value}
                              className={classNames(inputStyle.input, {
                                [inputStyle.red]: !!name.errors,
                                [inputStyle.lightBlue]: !!name.value,
                              })}
                              onChange={this.handleChangeName}
                              onFocus={handleFocusInputText}
                              placeholder={txt}/>
              }</FormattedMessage>
              {nameError}
            </article>
          </Element>
          <Element name="wishListTypeOption">
            <article className={style.wishListTypeOptions}>
              <FormattedMessage id="myWishLists.createModal.privacy.label">{
                txt => (<label className={style.privacy}>{txt}:</label>)
              }</FormattedMessage>
              {wishListTypeOptions.map((option: { [key: string]: string }, index: number) => (
                <article key={index}
                         className={classNames(style.option, { [style.selected]: wishListTypeOption.value === index })}
                         onClick={_ => handleChangeTypeOption(index)}>
                  <RadioButton name="wishListTypeOption"
                               value={index}
                               selected={wishListTypeOption.value}
                               handleChange={_ => {
                               }}/>
                  <FormattedMessage id={option.intl}>{
                    txt => <p>{txt}</p>
                  }</FormattedMessage>
                </article>),
              )}
              {wishListTypeOptionError}
            </article>
          </Element>
          <FormattedMessage
            id={mode === 'create' ? 'myWishLists.createModal.button.create' : 'myWishLists.createModal.button.save'}>{
            text => <button onClick={this.handleClickMainButton}
                            className={classNames(style.callToAction, buttonStyle.btn, buttonStyle.lightBlue)}>{text}</button>
          }</FormattedMessage>
          <FormattedMessage id="myWishLists.createModal.button.cancel">{
            text => <button onClick={handleCloseModal}
                            className={classNames(style.cancel, buttonStyle.btn, buttonStyle.greyInverse)}>{text}</button>
          }</FormattedMessage>
        </section>
      </Modal>);
  }
}

WishListModal.propTypes = {
  mode: PropTypes.string.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  openModal: PropTypes.bool,
  createWishList: PropTypes.func.isRequired,
  editWishList: PropTypes.func.isRequired,
  handleDeleteImage: PropTypes.func.isRequired,
  handleChangeImage: PropTypes.func.isRequired,
  handleChangeInputText: PropTypes.func.isRequired,
  handleFocusInputText: PropTypes.func.isRequired,
  handleChangeTypeOption: PropTypes.func.isRequired,
  name: PropTypes.object,
  image: PropTypes.object,
  wishListTypeOption: PropTypes.object,
  validateForm: PropTypes.func.isRequired,
};



// WEBPACK FOOTER //
// ./src/modules/my-wish-lists/components/WishListModal/WishListModal.jsx